import { useEffect, useState } from "react";
import Select from "react-select";
import { boundaryCRUD } from "../../actions/boundary";

/**
 * Cascading location filter: Island -> Region -> District -> Shehia -> Zone.
 * Calls onChange(boundaryId, zoneId) with the MOST SPECIFIC selection:
 *   - boundaryId = the deepest chosen admin boundary (Region/District/Shehia...).
 *     The server filters by that boundary's whole sub-tree.
 *   - zoneId = the chosen zone (exact), if any.
 * Backend already restricts results to the user's data scope, so a Shehia user
 * only ever sees their own boundaries here.
 */
export const ScopeFilter = ({ onChange }) => {
  const [islands, setIslands] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [shehias, setShehias] = useState([]);
  const [zones, setZones] = useState([]);

  const [island, setIsland] = useState(null);
  const [region, setRegion] = useState(null);
  const [district, setDistrict] = useState(null);
  const [shehia, setShehia] = useState(null);
  const [zone, setZone] = useState(null);

  // Scope: a non-national user (e.g. Shehia leader) is locked to their boundary —
  // we show its name as a chip and only let them drill DOWN (e.g. by Zone).
  const [scope, setScope] = useState(null);

  const opts = (list) =>
    (list || []).map((b) => ({
      value: b.id,
      label: b.boundary_name || b.name,
    }));

  // Most specific admin boundary chosen (zone is reported separately).
  const deepestBoundary = () =>
    (shehia || district || region || island || {}).value || null;

  const emit = (b, z) => onChange && onChange(b, z);

  useEffect(() => {
    boundaryCRUD.loadScopeMe()
      .then((r) => {
        const s = r.data;
        setScope(s);
        if (s && !s.is_national) {
          // Lock to the user's boundary; results start filtered to it. If it's a
          // Shehia, load its zones so they can drill down by zone.
          emit(s.boundary_id, null);
          if (s.level === 'Shehia') {
            boundaryCRUD.loadBoundaryByParent(s.boundary_id)
              .then((zr) => setZones(zr.data)).catch(() => {});
          }
        } else {
          // National (admin): full cascade starting from islands.
          boundaryCRUD.loadAllIslands().then((ir) => setIslands(ir.data)).catch(() => {});
        }
      })
      .catch(() => {
        boundaryCRUD.loadAllIslands().then((ir) => setIslands(ir.data)).catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIsland = (e) => {
    setIsland(e); setRegion(null); setDistrict(null); setShehia(null); setZone(null);
    setRegions([]); setDistricts([]); setShehias([]); setZones([]);
    if (e) boundaryCRUD.loadBoundaryByParent(e.value).then((r) => setRegions(r.data)).catch(() => {});
    emit(e ? e.value : null, null);
  };
  const onRegion = (e) => {
    setRegion(e); setDistrict(null); setShehia(null); setZone(null);
    setDistricts([]); setShehias([]); setZones([]);
    if (e) boundaryCRUD.loadBoundaryByParent(e.value).then((r) => setDistricts(r.data)).catch(() => {});
    emit(e ? e.value : (island ? island.value : null), null);
  };
  const onDistrict = (e) => {
    setDistrict(e); setShehia(null); setZone(null);
    setShehias([]); setZones([]);
    if (e) boundaryCRUD.loadBoundaryByParent(e.value).then((r) => setShehias(r.data)).catch(() => {});
    emit(e ? e.value : (region ? region.value : (island ? island.value : null)), null);
  };
  const onShehia = (e) => {
    setShehia(e); setZone(null); setZones([]);
    if (e) boundaryCRUD.loadBoundaryByParent(e.value).then((r) => setZones(r.data)).catch(() => {});
    emit(
      e ? e.value : (district ? district.value : (region ? region.value : (island ? island.value : null))),
      null
    );
  };
  const onZone = (e) => {
    setZone(e);
    emit(deepestBoundary(), e ? e.value : null);
  };

  const clearAll = () => {
    setIsland(null); setRegion(null); setDistrict(null); setShehia(null); setZone(null);
    setRegions([]); setDistricts([]); setShehias([]); setZones([]);
    emit(null, null);
  };

  const sel = { menuPortalTarget: document.body, styles: { menuPortal: (b) => ({ ...b, zIndex: 9999 }) } };
  const isScoped = scope && !scope.is_national;

  return (
    <div className="card border mb-3">
      <div className="card-body py-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="text-primary fw-semibold mb-0">
            <i className="ri-filter-3-line align-bottom me-1"></i>
            Filter by Location
          </h6>
          {!isScoped && (
            <button type="button" className="btn btn-sm btn-soft-secondary" onClick={clearAll}>
              <i className="ri-close-line align-bottom"></i> Clear
            </button>
          )}
        </div>

        {isScoped ? (
          /* Scoped user (e.g. Shehia leader): locked to their boundary. */
          <div className="row g-2 align-items-end">
            <div className="col-md-auto">
              <label className="form-label small mb-1">Your area</label>
              <div>
                <span className="badge bg-primary fs-6">
                  <i className="ri-map-pin-2-line align-bottom me-1"></i>
                  {scope.boundary_name} ({scope.level})
                </span>
              </div>
            </div>
            {scope.level === 'Shehia' && (
              <div className="col-md-4">
                <label className="form-label small mb-1">Zone</label>
                <Select {...sel} options={opts(zones)} value={zone}
                  onChange={(e) => { setZone(e); emit(scope.boundary_id, e ? e.value : null); }}
                  placeholder="All zones" isClearable isSearchable />
              </div>
            )}
          </div>
        ) : (
          /* National (admin): full cascade. */
          <div className="row g-2">
            <div className="col-md">
              <label className="form-label small mb-1">Island</label>
              <Select {...sel} options={opts(islands)} value={island} onChange={onIsland}
                placeholder="All" isClearable isSearchable />
            </div>
            <div className="col-md">
              <label className="form-label small mb-1">Region</label>
              <Select {...sel} options={opts(regions)} value={region} onChange={onRegion}
                placeholder="All" isClearable isSearchable isDisabled={!island} />
            </div>
            <div className="col-md">
              <label className="form-label small mb-1">District</label>
              <Select {...sel} options={opts(districts)} value={district} onChange={onDistrict}
                placeholder="All" isClearable isSearchable isDisabled={!region} />
            </div>
            <div className="col-md">
              <label className="form-label small mb-1">Shehia</label>
              <Select {...sel} options={opts(shehias)} value={shehia} onChange={onShehia}
                placeholder="All" isClearable isSearchable isDisabled={!district} />
            </div>
            <div className="col-md">
              <label className="form-label small mb-1">Zone</label>
              <Select {...sel} options={opts(zones)} value={zone} onChange={onZone}
                placeholder="All" isClearable isSearchable isDisabled={!shehia} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
